// contractsSlice.ts
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../API/axiosNeatex';
import type { APIResponse } from '../../Shared/shared';

interface ContractModel {
  id: string;
  personId: string;
  startDate: string;
  endDate: string;
  contractNumber: string;
  contractType: string;
  contractStatus: string;
  description: string;
  signedDate: string;
  signedBy: string;
  annualLeave: number;
  approvedFlag: boolean;
  ameStatus: number;
  isAme: boolean;
  personName?: string;
  personName2?: string;
}

interface ContractsState {
  contractsList: ContractModel[];
  currentContract: ContractModel | null;
  loading: boolean;
  error: string | null;
  loadingDetails: boolean;
  errorDetails: string | null;
}

const initialState: ContractsState = {
  contractsList: [],
  currentContract: null,
  loading: false,
  error: null,
  loadingDetails: false,
  errorDetails: null
};

// الحصول على قائمة العقود
export const getContractsList = createAsyncThunk(
  'contracts/getContractsList',
  async (personId: string, { rejectWithValue }) => {
    try {
      const params = { PersonId: personId };
      const { data } = await ntxAPI.get<APIResponse<ContractModel[]>>('Persons/GetHrPerContractsList', { params });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// الحصول على عقد محدد
export const getContractById = createAsyncThunk(
  'contracts/getContractById',
  async (contractId: string, { rejectWithValue }) => {
    try {
      const params = { Id: contractId };
      const { data } = await ntxAPI.get<APIResponse<ContractModel>>('Persons/GetPerContractsById', { params });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// إضافة عقد جديد
export const addContract = createAsyncThunk('contracts/addContract', async (contractData: any, { rejectWithValue }) => {
  try {
    const { data } = await ntxAPI.post<APIResponse<ContractModel>>('Persons/AddHrPerContracts', contractData);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

// تحديث عقد
export const updateContract = createAsyncThunk(
  'contracts/updateContract',
  async (contractData: ContractModel, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<ContractModel>>('Persons/UpdatHrPerContracts', contractData);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const contractsSlice = createSlice({
  name: 'contracts',
  initialState,
  reducers: {
    clearCurrentContract: state => {
      state.currentContract = null;
    },
    clearError: state => {
      state.error = null;
      state.errorDetails = null;
    }
  },
  extraReducers: builder => {
    builder
      // getContractsList
      .addCase(getContractsList.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getContractsList.fulfilled, (state, action: PayloadAction<APIResponse<ContractModel[]>>) => {
        state.loading = false;
        if (action.payload.succeeded) {
          state.contractsList = action.payload.data || [];
        }
      })
      .addCase(getContractsList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // getContractById
      .addCase(getContractById.pending, state => {
        state.loadingDetails = true;
        state.errorDetails = null;
      })
      .addCase(getContractById.fulfilled, (state, action: PayloadAction<APIResponse<ContractModel>>) => {
        state.loadingDetails = false;
        if (action.payload.succeeded) {
          state.currentContract = action.payload.data;
        }
      })
      .addCase(getContractById.rejected, (state, action) => {
        state.loadingDetails = false;
        state.errorDetails = action.payload as string;
      })

      // addContract
      // .addCase(addContract.pending, state => {
      //   state.loading = true;
      // })
      .addCase(addContract.fulfilled, (state, action: PayloadAction<APIResponse<ContractModel>>) => {
        state.loading = false;
        if (action.payload.succeeded && action.payload.data) {
          state.contractsList.push(action.payload.data);
        }
      })
      // .addCase(addContract.rejected, (state, action) => {
      //   state.loading = false;
      //   state.error = action.payload as string;
      // })

      // // updateContract
      // .addCase(updateContract.pending, state => {
      //   state.loading = true;
      // })
      .addCase(updateContract.fulfilled, (state, action: PayloadAction<APIResponse<ContractModel>>) => {
        state.loading = false;
        if (action.payload.succeeded && action.payload.data) {
          const index = state.contractsList.findIndex(contract => contract.id === action.payload.data?.id);
          if (index !== -1) {
            state.contractsList[index] = action.payload.data;
          }
          state.currentContract = action.payload.data;
        }
      })
      // .addCase(updateContract.rejected, (state, action) => {
      //   state.loading = false;
      //   state.error = action.payload as string;
      // });
  }
});

export const { clearCurrentContract, clearError } = contractsSlice.actions;
export default contractsSlice.reducer;
